import {
  api_portalOfficeListing_filter,
  ibow2_proto_PortalOfficeListing,
} from '@/init/swagger';
import NoImagePNG from '@/../public/no-image.png';
import { getPrefName } from '@/data/prefectures';
import dayjs from '@/init/dayjs';
import { pagesPath } from '@/lib/$path';
import { assortServiceItems } from '@/helpers/serviceCategory';
import { getPostCode } from '@/helpers/postCodeHelper';

type Props = {
  listing: ibow2_proto_PortalOfficeListing;
  searchFilters?: api_portalOfficeListing_filter[];
};

export const useLogic = ({ listing, searchFilters = [] }: Props) => {
  const thumbnailInfo = {
    image: listing.portal_office_listing_image?.image1_url || NoImagePNG.src,
    alt: listing.portal_office_listing_image?.image1 || 'NO IMAGE',
  };

  const {
    portal_office: portalOfficeData,
    portal_office_listing_services: portalOfficeServices,
  } = listing;

  const { services: serviceCategoryItems } = assortServiceItems({
    items: portalOfficeServices,
    masterItems: searchFilters,
  });

  const hasServiceCategoryItems =
    serviceCategoryItems && serviceCategoryItems.length > 0;

  if (!portalOfficeData) {
    throw new Error('portal_office is undefined');
  }

  const {
    pref = 0,
    city = '',
    area = '',
    building = '',
    tel_area,
    tel_city,
    tel_num,
    post_code,
  } = portalOfficeData;

  const postCode = post_code ? `〒${getPostCode(post_code)}` : '';
  const address = `${getPrefName(pref)}${city}${area}${building}`;
  const tel =
    tel_area && tel_city && tel_num ? `${tel_area}-${tel_city}-${tel_num}` : '';

  const portalOfficeInfo = {
    name: listing.portal_office?.name,
    postCode,
    address,
    tel,
  };

  const cardHref = `/public/stations/${portalOfficeData.id}`;

  const formatBusinessHours = (
    start: string | undefined | null,
    end: string | undefined | null,
  ) => {
    if (!start && !end) {
      return '';
    }
    const startDate = start ? dayjs(start).format('HH:mm') : '';
    const endDate = end ? dayjs(end).format('HH:mm') : '';
    return `${startDate} 〜 ${endDate}`;
  };

  const weekdayBusinessHours = formatBusinessHours(
    listing.portal_office_listing_business_hours?.weekday_start_at,
    listing.portal_office_listing_business_hours?.weekday_end_at,
  );

  const saturdayBusinessHours = formatBusinessHours(
    listing.portal_office_listing_business_hours?.saturday_start_at,
    listing.portal_office_listing_business_hours?.saturday_end_at,
  );
  const sundayBusinessHours = formatBusinessHours(
    listing.portal_office_listing_business_hours?.sunday_start_at,
    listing.portal_office_listing_business_hours?.sunday_end_at,
  );

  const businessHours = {
    平日: weekdayBusinessHours,
    土曜: saturdayBusinessHours,
    日曜: sundayBusinessHours,
  };

  const businessHourStr = Object.keys(businessHours).reduce((result, key) => {
    if (businessHours[key as keyof typeof businessHours]) {
      if (!result) {
        return `${key}：${businessHours[key as keyof typeof businessHours]}`;
      }
      return (
        result + `、${key}：${businessHours[key as keyof typeof businessHours]}`
      );
    }
    return result;
  }, '');

  const publishedOnStr = listing.portal_office?.listing_released_at
    ? dayjs(listing.portal_office?.listing_released_at).format('YY.M.D')
    : '-';
  const updatedOnStr = listing.portal_office?.listing_updated_at
    ? dayjs(listing.portal_office?.listing_updated_at).format('YY.M.D')
    : '-';

  const isDxStation = !!listing.portal_office?.ibow_office_id;
  const isCarelogood = listing.portal_office?.is_carelogood === true;

  const onClickRequest: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();

    window.open(
      pagesPath.app.requests.create
        ._portalOfficeId(listing?.portal_office_id as number)
        .$url().path,
      '_blank',
    );
  };

  return {
    thumbnailInfo,
    portalOfficeInfo,
    cardHref,
    businessHourStr,
    publishedOnStr,
    updatedOnStr,
    isDxStation,
    isCarelogood,
    hasServiceCategoryItems,
    serviceCategoryItems,
    onClickRequest,
  };
};
